export type HcConfig = {
  monthlyDefaultPrice: number
  hcaptchaSiteKey: string | null
  hcaptchaEnabled: boolean
  devCaptchaResponse: string | null
  flash: { success?: string; error?: string; notice?: string; alert?: string; warning?: string }
}

const DEFAULTS: HcConfig = {
  monthlyDefaultPrice: 20000,
  hcaptchaSiteKey: null,
  hcaptchaEnabled: true,
  devCaptchaResponse: null,
  flash: {},
}

declare global {
  interface Window {
    __HC_CONFIG__?: Partial<HcConfig>
    mixpanel?: {
      track: (event: string, props?: Record<string, unknown>, options?: TrackOptions) => void
      identify: (id: string) => void
      register?: (props: Record<string, unknown>) => void
      reset: () => void
      people: { set: (props: Record<string, unknown>) => void }
    }
  }
}

export type TrackOptions = { transport?: 'sendBeacon' | 'xhr' }

export function clearServerFlash() {
  if (window.__HC_CONFIG__) {
    window.__HC_CONFIG__ = { ...window.__HC_CONFIG__, flash: {} }
  }
}

export function getConfig(): HcConfig {
  return { ...DEFAULTS, ...window.__HC_CONFIG__ }
}

function compactProps(props: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(props).filter(([, value]) => value !== null && value !== undefined))
}

// Tracking is never allowed to break the page: a snippet that failed to load, an SDK that
// throws, or a bad property is swallowed here so the handler that called us carries on.
function guard(send: () => void) {
  try {
    send()
  } catch {
    return
  }
}

/** Sends an event with its null and undefined properties omitted rather than booked as empty values. */
export function trackEvent(event: string, props?: Record<string, unknown>, options?: TrackOptions) {
  guard(() => window.mixpanel?.track(event, props && compactProps(props), options))
}

/** Registers properties Mixpanel attaches to every later event from this browser. */
export function registerSuperProperties(props: Record<string, unknown>) {
  guard(() => window.mixpanel?.register?.(compactProps(props)))
}

export function setProfileProperties(props: Record<string, unknown>) {
  guard(() => window.mixpanel?.people.set(compactProps(props)))
}

export function resetIdentity() {
  guard(() => window.mixpanel?.reset())
}
